// LoginEdit.jsx

// LoginEdit.js

import React, { useState } from 'react';

import { useUser } from 'context/UserContext';

// Firebase
import {
    reauthenticateWithCredential,
    EmailAuthProvider,
    updateEmail,
    updatePassword
} from 'firebase/auth';

// CSS
import './LoginEdit.css';

// Components
import Button from 'components/button/Button';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';
import { auth } from 'firebaseConfig';

/**
 * LoginEdit
 * 
 * Allows the user to securely update their login email or password.
 *
 * @returns {ReactNode} The rendered component.
 */
const LoginEdit = () => {

    // ----- CONTEXT ----- //

    // Global Context

    const { user } = useUser();

    // ----- STATE ----- //

    // State

    const [email, setEmail] = useState(user?.data_profile?.email || '');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState('');

    // ----- HANDLERS ----- //

    /**
     * Handles login update: re-authenticate, then update email/password
     */

    /**
 * Handles submit.
 */

    const handleSubmit = async () => {
        setStatus(null);

        if (!currentPassword) {
            setStatus('Please enter your current password to confirm.');
            return;
        }

        if (newPassword && newPassword !== confirmPassword) {
            setStatus('New passwords do not match.');
            return;
        }

        try {
            const authUser = auth.currentUser;

            // Re-authenticate
            const credential = EmailAuthProvider.credential(
                authUser.email,
                currentPassword
            );
            await reauthenticateWithCredential(authUser, credential);

            // Update email if changed
            if (email !== authUser.email) {
                await updateEmail(authUser, email);
            }

            // Update password if provided
            if (newPassword) {
                await updatePassword(authUser, newPassword);
            }

            setStatus('Login information updated successfully!');
        } catch (error) {
            console.error(error);
            setStatus(error.message || 'An error occurred while updating login info.');
        }
    };

    // ----- RENDER ----- //

    return (
        <div
          className="login-edit-container"
        >

            {/* FIELD WRAPPER */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="Login Email" />
                <TextInput
                  type="email"
                  value={email}
                  width={350}
                  onChange={(e) => setEmail(e.target.value)}
                />
            </div>

            {/* FIELD WRAPPER */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="Current Password (required)" />
                <TextInput
                  type="password"
                  value={currentPassword}
                  width={350}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
            </div>

            {/* FIELD WRAPPER */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="New Password (optional)" />
                <TextInput
                  type="password"
                  value={newPassword}
                  width={350}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
            </div>

            {/* FIELD WRAPPER */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="Confirm New Password" />
                <TextInput
                  type="password"
                  value={confirmPassword}
                  width={350}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
            </div>

            {status && <div className="login-edit-status">{status}</div>}

            {/* BUTTONS */}
            <div className="login-edit-buttons">
                <Button
                  iconKey="check"
                  label="Save Changes"
                  onClick={handleSubmit}
                />
            </div>

        </div>
    );
};

// Dev Tools
LoginEdit.displayName = 'LoginEdit';

export default LoginEdit;
